import {useMemo,useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {api,client} from '../lib/api';
import {openDraftAttachment} from '../lib/communication-office';
import {useJob,useJobInstalls} from '../hooks/useReads';
import {JobDocumentActions} from './job-document-actions';
import {JobDeliverables} from './job-deliverables';
import {JobStrataPack} from './job-strata-pack';
import {OfficeInstallConfirmation} from './office-install-confirmation';
import {useT} from '../lib/i18n';
import {recordedInstallDays} from '../lib/job-history';
import type {Install} from '../hooks/slice-a';

// The list carries file names only (services/api/src/communication-list.ts); a file is fetched when opened.
type Attachment={index:number;name:string;content_type:string|null};
type Communication={id:string;context_ref:string|null;subject:string;state:string;created_at:string;attachments:Attachment[]};
const documentKind=(name:string)=>name.startsWith('Completion_Report_')?'completion':name.startsWith('Warranty_Statement_')?'warranty':null;

/** The Job door shows saved customer drafts, historical PDFs and private photo
 * evidence beside the report action. The general archive beneath it is only
 * the additional PO/installer-report archive, not the whole Job document set. */
export function JobDocumentWorkspace({jobRef}:{jobRef:string}){
  const {t}=useT();
  const job=useJob(jobRef),installs=useJobInstalls(jobRef);
  const drafts=useQuery({queryKey:['job',jobRef,'document-drafts'],queryFn:()=>api.communications(client,{context_ref:jobRef,state:'draft'})});
  const [openError,setOpenError]=useState('');
  const latest=useMemo(()=>{
    const all=(drafts.data??[]) as unknown as Communication[];
    const selected=new Map<string,Communication>();
    for(const row of all.filter(r=>r.context_ref===jobRef&&r.state==='draft').sort((a,b)=>b.created_at.localeCompare(a.created_at))){
      for(const file of Array.isArray(row.attachments)?row.attachments:[]){
        const kind=documentKind(file.name);
        if(kind&&!selected.has(kind))selected.set(kind,row);
      }
    }
    return [...selected.values()];
  },[drafts.data,jobRef]);
  const j=job.data as {ref:string;contact_id:string|null;address:string|null;product:string|null}|undefined;
  const installRows=(installs.data??[]) as {id:string}[];
  const installationId=installRows.length===1?installRows[0]!.id:null;

  async function open(row:Communication,file:Attachment){
    setOpenError('');
    try{
      if(file.content_type!=='application/pdf')throw Error('PDF unavailable');
      await openDraftAttachment(row.id,file.index);
    }catch{setOpenError(t('job.documentsOpenFailed'));}
  }

  return <section className="mb-5 space-y-4 rounded-lg border bg-card p-4" aria-label={t('job.documentsTitle')}>
    <div><h2 className="font-display text-lg font-semibold">{t('job.documentsTitle')} · {jobRef}</h2><p className="text-sm text-muted-foreground">{t('job.documentsDraftHint')}</p></div>
    {drafts.isPending?<p role="status">{t('job.documentsLoading')}</p>:drafts.isError?<p role="alert">{t('job.documentsLoadFailed')}</p>:
      latest.length?<ul className="divide-y rounded border">{latest.map(row=><li key={row.id} className="p-3 text-sm"><p className="font-medium">{row.subject} · {t('job.documentsDraft')}</p>{row.attachments.map((file,index)=><div key={`${file.name}-${index}`} className="mt-2 flex flex-wrap items-center justify-between gap-2"><span>{file.name}</span><button type="button" className="rounded border px-3 py-1.5 text-primary" onClick={()=>void open(row,file)}>{t('job.documentsOpen')}</button></div>)}</li>)}</ul>:<p className="text-sm text-muted-foreground">{t('job.documentsEmpty')}</p>}
    {openError&&<p role="alert" className="text-sm text-destructive">{openError}</p>}
    {j&&<JobDocumentActions job={{ref:j.ref,contact_id:j.contact_id,address:j.address,product:j.product,installation_id:installationId,crew_days:recordedInstallDays(installs.data as unknown as Install[]|undefined)}}/>}
    <JobStrataPack jobRef={jobRef}/>
    <JobDeliverables jobRef={jobRef}/>
    {installationId&&<OfficeInstallConfirmation jobRef={jobRef} installationId={installationId}/>}
  </section>;
}
